import { ReactNode, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { formatDate, FULL_DATE_TIME_FORMAT } from '@/helpers/dateHelpers/dateHelpers';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { SecureLinkType } from '@/types/myVaultTypes';
import { SecureLinkActionType } from '@/api/v1/organization/my-vault/secureLinkActionApi';
import { MY_VAULT_QUERY_KEYS, runSecureLinkAction } from '../api/api';
import { SecureLinkStatus } from './SecureLinkStatus';
import { InlineAction } from '../styles';
import { TableHeadNew } from '@/components-new/TableNew/TableHeadNew';
import { TableHeadCellNew } from '@/components-new/TableNew/TableHeadCellNew';
import { TableNew } from '@/components-new/TableNew/TableNew';
import { TableBodyNew, TableCellNew, TableRowNew } from '@/components-new/TableNew/styles';

type SecureLinksListType = {
  list: SecureLinkType[];
  header?: ReactNode;
  onOpenVerification: (link: SecureLinkType) => void;
  isLoading: boolean;
};

const STAMP_LABEL = { verified: 'Verified', opened: 'Opened', revoked: 'Revoked' };

export const SecureLinksList = ({ header, list, isLoading, onOpenVerification }: SecureLinksListType) => {
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationKey: ['secure_link_action'],
    mutationFn: ({ id, action }: { id: string; action: SecureLinkActionType }) => runSecureLinkAction(id, action),
    onSuccess(response, { action }) {
      if (response?.error) return showSnackbarResponseError(response.error);

      if (action === 'revoke') amplitudeService.logEvent(AMPLITUDE_EVENTS.RevokeSecureLinkSuccess);

      queryClient.invalidateQueries({ queryKey: [MY_VAULT_QUERY_KEYS.links] });
    },
  });

  const visible = list.filter((link) => showArchived || !link.archived);
  const archivedCount = list.filter((link) => link.archived).length;

  const stampText = (link: SecureLinkType) =>
    link.stamp ? `${STAMP_LABEL[link.stamp.kind]} ${formatDate(link.stamp.at, FULL_DATE_TIME_FORMAT)}` : '';

  return (
    <TableNew
      header={
        <Box display='flex' justifyContent='space-between'>
          <Typography fontWeight='semibold'>{header}</Typography>
          <Typography variant='bodySmall' marginBottom='8px'>
            Revoke any link until the recipient verifies. Every open is logged.
          </Typography>
        </Box>
      }
      isLoading={isLoading}
      itemsCount={visible.length}
      noItems='No links match.'
      footer={
        archivedCount > 0 && (
          <InlineAction onClick={() => setShowArchived((value) => !value)}>
            {showArchived ? 'Hide archived links' : `Show archived links (${archivedCount})`}
          </InlineAction>
        )
      }
    >
      <>
        <TableHeadNew>
          <TableHeadCellNew>ACCOUNT</TableHeadCellNew>
          <TableHeadCellNew>RAIL</TableHeadCellNew>
          <TableHeadCellNew>SHARED WITH</TableHeadCellNew>
          <TableHeadCellNew>CONTROLS</TableHeadCellNew>
          <TableHeadCellNew name='date_created' hasSort>
            SENT
          </TableHeadCellNew>
          <TableHeadCellNew>STATUS</TableHeadCellNew>
          <TableHeadCellNew></TableHeadCellNew>
        </TableHeadNew>
        <TableBodyNew>
          {visible.map((link) => (
            <TableRowNew key={link.id}>
              <TableCellNew>
                <Typography variant='bodySmall' fontWeight='medium'>
                  {link.account_label}
                </Typography>
              </TableCellNew>
              <TableCellNew>{link.rail_label}</TableCellNew>
              <TableCellNew>{link.sent_to}</TableCellNew>
              <TableCellNew>{link.controls}</TableCellNew>
              <TableCellNew>{formatDate(link.date_created, FULL_DATE_TIME_FORMAT)}</TableCellNew>
              <TableCellNew>
                <SecureLinkStatus link={link} />
              </TableCellNew>
              <TableCellNew>
                <Box display='flex' flexDirection='column' gap={'8px'} alignItems='flex-start'>
                  {link.can_revoke && (
                    <InlineAction
                      $danger
                      disabled={isPending}
                      onClick={() => mutate({ id: link.id, action: 'revoke' })}
                    >
                      Revoke
                    </InlineAction>
                  )}
                  {link.can_resend && (
                    <>
                      <InlineAction disabled={isPending} onClick={() => mutate({ id: link.id, action: 'resend' })}>
                        Resend
                      </InlineAction>
                      <InlineAction disabled={isPending} onClick={() => mutate({ id: link.id, action: 'archive' })}>
                        Archive
                      </InlineAction>
                    </>
                  )}
                  {link.has_verification ? (
                    <InlineAction onClick={() => onOpenVerification(link)}>{stampText(link)}</InlineAction>
                  ) : (
                    link.stamp && <Typography variant='bodySmall2'>{stampText(link)}</Typography>
                  )}
                </Box>
              </TableCellNew>
            </TableRowNew>
          ))}
        </TableBodyNew>
      </>
    </TableNew>
  );
};
